/**
 * Builds FlatBuffer Object API ValuedMessageT instances from composite keys.
 *
 * Composite key format: "<category>:<variant>:<enumValue>"
 *   e.g., "status:normal:7" for Status::Power (Normal variant, elevator default)
 *
 * All 9 LN2 message categories follow the same 4-level nesting:
 *   ValuedMessageT -> MessageT -> BitMask/OneHotMessageT -> CategoryMessageT
 *     -> DeviceCategoryMessageT -> Normal/ValuedCategoryT -> enum value
 *
 * Device type defaults to "elevator" at config time. At expansion time the
 * server wraps the ValuedMessageT in FrontEndMessageT with bank_id/lift_id,
 * adjusting the device wrapper if the bank is escalator.
 */
import type * as flatbuffers from "flatbuffers";

import { ValuedMessageT } from "../generated/ln2/valued-message";
import { MessageT } from "../generated/ln2/message";
import { EncodedMessageContent } from "../generated/ln2/encoded-message-content";
import { BitMaskMessageT } from "../generated/ln2/bit-mask-message";
import { OneHotMessageT } from "../generated/ln2/one-hot-message";
import { MessageContent } from "../generated/ln2/message-content";
import { Value } from "../generated/value/value";

// -- Status --
import { StatusMessageT } from "../generated/status/status-message";
import { ElevatorStatusMessageT } from "../generated/status/elevator-status-message";
import { EscalatorStatusMessageT } from "../generated/status/escalator-status-message";
import { DeviceStatusContent } from "../generated/status/device-status-content";
import { StatusVariant } from "../generated/status/status-variant";
import { NormalStatusT } from "../generated/status/normal-status";
import { ValuedStatusT } from "../generated/status/valued-status";

// -- Traffic --
import { TrafficMessageT } from "../generated/traffic/traffic-message";
import { ElevatorTrafficMessageT } from "../generated/traffic/elevator-traffic-message";
import { EscalatorTrafficMessageT } from "../generated/traffic/escalator-traffic-message";
import { DeviceTrafficMessage } from "../generated/traffic/device-traffic-message";
import { TrafficVariant } from "../generated/traffic/traffic-variant";
import { NormalTrafficT } from "../generated/traffic/normal-traffic";
import { ValuedTrafficT } from "../generated/traffic/valued-traffic";

// -- Fault --
import { FaultMessageT } from "../generated/fault/fault-message";
import { ElevatorFaultMessageT } from "../generated/fault/elevator-fault-message";
import { EscalatorFaultMessageT } from "../generated/fault/escalator-fault-message";
import { FaultContent } from "../generated/fault/fault-content";
import { FaultVariant } from "../generated/fault/fault-variant";
import { NormalFaultT } from "../generated/fault/normal-fault";
import { ValuedFaultT } from "../generated/fault/valued-fault";

// -- Control --
import { ControlMessageT } from "../generated/control/control-message";
import { ElevatorControlMessageT } from "../generated/control/elevator-control-message";
import { EscalatorControlMessageT } from "../generated/control/escalator-control-message";
import { DeviceControlMessage } from "../generated/control/device-control-message";
import { ControlVariant } from "../generated/control/control-variant";
import { NormalControlT } from "../generated/control/normal-control";
import { ValuedControlT } from "../generated/control/valued-control";

// -- Service --
import { ServiceMessageT } from "../generated/service/service-message";
import { ElevatorServiceMessageT } from "../generated/service/elevator-service-message";
import { EscalatorServiceMessageT } from "../generated/service/escalator-service-message";
import { DeviceServiceContent } from "../generated/service/device-service-content";
import { ServiceVariant } from "../generated/service/service-variant";
import { NormalServiceT } from "../generated/service/normal-service";
import { ValuedServiceT } from "../generated/service/valued-service";

// -- Usage --
import { UsageMessageT } from "../generated/usage/usage-message";
import { ElevatorUsageMessageT } from "../generated/usage/elevator-usage-message";
import { EscalatorUsageMessageT } from "../generated/usage/escalator-usage-message";
import { DeviceUsageContent } from "../generated/usage/device-usage-content";
import { UsageVariant } from "../generated/usage/usage-variant";
import { NormalUsageT } from "../generated/usage/normal-usage";
import { ValuedUsageT } from "../generated/usage/valued-usage";

// -- Group Supervisory Status --
import { GroupSupervisoryStatusMessageT } from "../generated/group-supervisory-status/group-supervisory-status-message";
import { ElevatorGroupSupervisoryStatusMessageT } from "../generated/group-supervisory-status/elevator-group-supervisory-status-message";
import { EscalatorGroupSupervisoryStatusMessageT } from "../generated/group-supervisory-status/escalator-group-supervisory-status-message";
import { DeviceGroupSupervisoryStatusContent } from "../generated/group-supervisory-status/device-group-supervisory-status-content";
import { GroupSupervisoryStatusVariant } from "../generated/group-supervisory-status/group-supervisory-status-variant";
import { NormalGroupSupervisoryStatusT } from "../generated/group-supervisory-status/normal-group-supervisory-status";
import { ValuedGroupSupervisoryStatusT } from "../generated/group-supervisory-status/valued-group-supervisory-status";

// -- Group Control --
import { GroupControlMessageT } from "../generated/group-control/group-control-message";
import { ElevatorGroupControlMessageT } from "../generated/group-control/elevator-group-control-message";
import { EscalatorGroupControlMessageT } from "../generated/group-control/escalator-group-control-message";
import { DeviceGroupControlContent } from "../generated/group-control/device-group-control-content";
import { GroupControlVariant } from "../generated/group-control/group-control-variant";
import { NormalGroupControlT } from "../generated/group-control/normal-group-control";
import { ValuedGroupControlT } from "../generated/group-control/valued-group-control";

// -- Controller Info --
import { ControllerInfoMessageT } from "../generated/controller-info/controller-info-message";
import { ElevatorControllerInfoMessageT } from "../generated/controller-info/elevator-controller-info-message";
import { EscalatorControllerInfoMessageT } from "../generated/controller-info/escalator-controller-info-message";
import { DeviceControllerInfoContent } from "../generated/controller-info/device-controller-info-content";
import { ControllerInfoVariant } from "../generated/controller-info/controller-info-variant";
import { NormalControllerInfoT } from "../generated/controller-info/normal-controller-info";
import { ValuedControllerInfoT } from "../generated/controller-info/valued-controller-info";

import {
  parseCompositeKey,
  categoryKeyToMessageContent,
  messageContentToCategoryKey,
  type MessageVariant,
  type DeviceType,
} from "./ln2MessageOptions";

// =====================================================================
// Category builder registry
// =====================================================================

/**
 * Each category defines how to build the inner category-specific message
 * from an enum value, variant, and device type. The outer ValuedMessageT
 * and BitMask/OneHot wrapping is handled generically.
 *
 * `extractEnum` is the strongly-typed reverse of `buildInner` — it reads
 * variant and enum value from the generated *T type without duck typing.
 */
interface CategorySpec {
  encodedMessageContent: EncodedMessageContent;
  buildInner: (
    enumValue: number,
    variant: MessageVariant,
    deviceType: DeviceType,
  ) => flatbuffers.IGeneratedObject;
  extractEnum: (
    inner: flatbuffers.IGeneratedObject,
  ) => { variant: MessageVariant; enumValue: number } | null;
}

function buildStatus(
  enumValue: number,
  variant: MessageVariant,
  deviceType: DeviceType,
): StatusMessageT {
  const leaf =
    variant === "normal"
      ? new NormalStatusT(enumValue)
      : new ValuedStatusT(enumValue, Value.NONE, null);
  const variantDisc =
    variant === "normal" ? StatusVariant.NormalStatus : StatusVariant.ValuedStatus;
  const deviceMsg =
    deviceType === "elevator"
      ? new ElevatorStatusMessageT(variantDisc, leaf)
      : new EscalatorStatusMessageT(variantDisc, leaf);
  const deviceDisc =
    deviceType === "elevator"
      ? DeviceStatusContent.ElevatorStatusMessage
      : DeviceStatusContent.EscalatorStatusMessage;
  return new StatusMessageT(deviceDisc, deviceMsg);
}

function buildTraffic(
  enumValue: number,
  variant: MessageVariant,
  deviceType: DeviceType,
): TrafficMessageT {
  const leaf =
    variant === "normal"
      ? new NormalTrafficT(enumValue)
      : new ValuedTrafficT(enumValue, Value.NONE, null);
  const variantDisc =
    variant === "normal" ? TrafficVariant.NormalTraffic : TrafficVariant.ValuedTraffic;
  // ElevatorTrafficMessageT has a 3rd `position` param (default 0)
  const deviceMsg =
    deviceType === "elevator"
      ? new ElevatorTrafficMessageT(variantDisc, leaf, 0)
      : new EscalatorTrafficMessageT(variantDisc, leaf);
  const deviceDisc =
    deviceType === "elevator"
      ? DeviceTrafficMessage.ElevatorTrafficMessage
      : DeviceTrafficMessage.EscalatorTrafficMessage;
  return new TrafficMessageT(deviceDisc, deviceMsg);
}

function buildFault(
  enumValue: number,
  variant: MessageVariant,
  deviceType: DeviceType,
): FaultMessageT {
  const leaf =
    variant === "normal"
      ? new NormalFaultT(enumValue)
      : new ValuedFaultT(enumValue, Value.NONE, null);
  const variantDisc = variant === "normal" ? FaultVariant.NormalFault : FaultVariant.ValuedFault;
  const deviceMsg =
    deviceType === "elevator"
      ? new ElevatorFaultMessageT(variantDisc, leaf)
      : new EscalatorFaultMessageT(variantDisc, leaf);
  const deviceDisc =
    deviceType === "elevator"
      ? FaultContent.ElevatorFaultMessage
      : FaultContent.EscalatorFaultMessage;
  return new FaultMessageT(deviceDisc, deviceMsg);
}

function buildControl(
  enumValue: number,
  variant: MessageVariant,
  deviceType: DeviceType,
): ControlMessageT {
  const leaf =
    variant === "normal"
      ? new NormalControlT(enumValue)
      : new ValuedControlT(enumValue, Value.NONE, null);
  const variantDisc =
    variant === "normal" ? ControlVariant.NormalControl : ControlVariant.ValuedControl;
  // ElevatorControlMessageT has a 3rd `position` param (default 0)
  const deviceMsg =
    deviceType === "elevator"
      ? new ElevatorControlMessageT(variantDisc, leaf, 0)
      : new EscalatorControlMessageT(variantDisc, leaf);
  const deviceDisc =
    deviceType === "elevator"
      ? DeviceControlMessage.ElevatorControlMessage
      : DeviceControlMessage.EscalatorControlMessage;
  return new ControlMessageT(deviceDisc, deviceMsg);
}

function buildService(
  enumValue: number,
  variant: MessageVariant,
  deviceType: DeviceType,
): ServiceMessageT {
  const leaf =
    variant === "normal"
      ? new NormalServiceT(enumValue)
      : new ValuedServiceT(enumValue, Value.NONE, null);
  const variantDisc =
    variant === "normal" ? ServiceVariant.NormalService : ServiceVariant.ValuedService;
  const deviceMsg =
    deviceType === "elevator"
      ? new ElevatorServiceMessageT(variantDisc, leaf)
      : new EscalatorServiceMessageT(variantDisc, leaf);
  const deviceDisc =
    deviceType === "elevator"
      ? DeviceServiceContent.ElevatorServiceMessage
      : DeviceServiceContent.EscalatorServiceMessage;
  return new ServiceMessageT(deviceDisc, deviceMsg);
}

function buildUsage(
  enumValue: number,
  variant: MessageVariant,
  deviceType: DeviceType,
): UsageMessageT {
  const leaf =
    variant === "normal"
      ? new NormalUsageT(enumValue)
      : new ValuedUsageT(enumValue, Value.NONE, null);
  const variantDisc = variant === "normal" ? UsageVariant.NormalUsage : UsageVariant.ValuedUsage;
  const deviceMsg =
    deviceType === "elevator"
      ? new ElevatorUsageMessageT(variantDisc, leaf)
      : new EscalatorUsageMessageT(variantDisc, leaf);
  const deviceDisc =
    deviceType === "elevator"
      ? DeviceUsageContent.ElevatorUsageMessage
      : DeviceUsageContent.EscalatorUsageMessage;
  return new UsageMessageT(deviceDisc, deviceMsg);
}

function buildGroupSupervisoryStatus(
  enumValue: number,
  variant: MessageVariant,
  deviceType: DeviceType,
): GroupSupervisoryStatusMessageT {
  const leaf =
    variant === "normal"
      ? new NormalGroupSupervisoryStatusT(enumValue)
      : new ValuedGroupSupervisoryStatusT(enumValue, Value.NONE, null);
  const variantDisc =
    variant === "normal"
      ? GroupSupervisoryStatusVariant.NormalGroupSupervisoryStatus
      : GroupSupervisoryStatusVariant.ValuedGroupSupervisoryStatus;
  const deviceMsg =
    deviceType === "elevator"
      ? new ElevatorGroupSupervisoryStatusMessageT(variantDisc, leaf)
      : new EscalatorGroupSupervisoryStatusMessageT(variantDisc, leaf);
  const deviceDisc =
    deviceType === "elevator"
      ? DeviceGroupSupervisoryStatusContent.ElevatorGroupSupervisoryStatusMessage
      : DeviceGroupSupervisoryStatusContent.EscalatorGroupSupervisoryStatusMessage;
  return new GroupSupervisoryStatusMessageT(deviceDisc, deviceMsg);
}

function buildGroupControl(
  enumValue: number,
  variant: MessageVariant,
  deviceType: DeviceType,
): GroupControlMessageT {
  const leaf =
    variant === "normal"
      ? new NormalGroupControlT(enumValue)
      : new ValuedGroupControlT(enumValue, Value.NONE, null);
  const variantDisc =
    variant === "normal"
      ? GroupControlVariant.NormalGroupControl
      : GroupControlVariant.ValuedGroupControl;
  const deviceMsg =
    deviceType === "elevator"
      ? new ElevatorGroupControlMessageT(variantDisc, leaf)
      : new EscalatorGroupControlMessageT(variantDisc, leaf);
  const deviceDisc =
    deviceType === "elevator"
      ? DeviceGroupControlContent.ElevatorGroupControlMessage
      : DeviceGroupControlContent.EscalatorGroupControlMessage;
  return new GroupControlMessageT(deviceDisc, deviceMsg);
}

function buildControllerInfo(
  enumValue: number,
  variant: MessageVariant,
  deviceType: DeviceType,
): ControllerInfoMessageT {
  // NormalControllerInfoT takes (messageType: ControllerInfoContent, message: ...T|null)
  // ValuedControllerInfoT takes (messageType, message, valueType, value)
  // At config time, inner message content is null (populated at runtime)
  const leaf =
    variant === "normal"
      ? new NormalControllerInfoT(enumValue, null)
      : new ValuedControllerInfoT(enumValue, null, Value.NONE, null);
  const variantDisc =
    variant === "normal"
      ? ControllerInfoVariant.NormalControllerInfo
      : ControllerInfoVariant.ValuedControllerInfo;
  const deviceMsg =
    deviceType === "elevator"
      ? new ElevatorControllerInfoMessageT(variantDisc, leaf)
      : new EscalatorControllerInfoMessageT(variantDisc, leaf);
  const deviceDisc =
    deviceType === "elevator"
      ? DeviceControllerInfoContent.ElevatorControllerInfoMessage
      : DeviceControllerInfoContent.EscalatorControllerInfoMessage;
  return new ControllerInfoMessageT(deviceDisc, deviceMsg);
}

// -- Per-category strongly-typed extractors (reverse of buildXxx) --

function extractStatus(inner: flatbuffers.IGeneratedObject) {
  const cat = inner as StatusMessageT;
  const dev = cat.status as ElevatorStatusMessageT | EscalatorStatusMessageT | null;
  if (!dev) return null;
  const leaf = "status" in dev ? (dev as ElevatorStatusMessageT).status : null;
  if (!leaf) return null;
  const variant: MessageVariant =
    "statusType" in dev && (dev as ElevatorStatusMessageT).statusType === StatusVariant.ValuedStatus
      ? "valued"
      : "normal";
  return { variant, enumValue: (leaf as NormalStatusT).message };
}

function extractTraffic(inner: flatbuffers.IGeneratedObject) {
  const cat = inner as TrafficMessageT;
  const dev = cat.message as ElevatorTrafficMessageT | EscalatorTrafficMessageT | null;
  if (!dev) return null;
  const leaf = "traffic" in dev ? (dev as ElevatorTrafficMessageT).traffic : null;
  if (!leaf) return null;
  const variant: MessageVariant =
    "trafficType" in dev &&
    (dev as ElevatorTrafficMessageT).trafficType === TrafficVariant.ValuedTraffic
      ? "valued"
      : "normal";
  return { variant, enumValue: (leaf as NormalTrafficT).message };
}

function extractFault(inner: flatbuffers.IGeneratedObject) {
  const cat = inner as FaultMessageT;
  const dev = cat.fault as ElevatorFaultMessageT | EscalatorFaultMessageT | null;
  if (!dev) return null;
  const leaf = "fault" in dev ? (dev as ElevatorFaultMessageT).fault : null;
  if (!leaf) return null;
  const variant: MessageVariant =
    "faultType" in dev && (dev as ElevatorFaultMessageT).faultType === FaultVariant.ValuedFault
      ? "valued"
      : "normal";
  return { variant, enumValue: (leaf as NormalFaultT).message };
}

function extractControl(inner: flatbuffers.IGeneratedObject) {
  const cat = inner as ControlMessageT;
  const dev = cat.control as ElevatorControlMessageT | EscalatorControlMessageT | null;
  if (!dev) return null;
  const leaf = "control" in dev ? (dev as ElevatorControlMessageT).control : null;
  if (!leaf) return null;
  const variant: MessageVariant =
    "controlType" in dev &&
    (dev as ElevatorControlMessageT).controlType === ControlVariant.ValuedControl
      ? "valued"
      : "normal";
  return { variant, enumValue: (leaf as NormalControlT).message };
}

function extractService(inner: flatbuffers.IGeneratedObject) {
  const cat = inner as ServiceMessageT;
  const dev = cat.message as ElevatorServiceMessageT | EscalatorServiceMessageT | null;
  if (!dev) return null;
  const leaf = "message" in dev ? (dev as ElevatorServiceMessageT).message : null;
  if (!leaf) return null;
  const variant: MessageVariant =
    "messageType" in dev &&
    (dev as ElevatorServiceMessageT).messageType === ServiceVariant.ValuedService
      ? "valued"
      : "normal";
  return { variant, enumValue: (leaf as NormalServiceT).message };
}

function extractUsage(inner: flatbuffers.IGeneratedObject) {
  const cat = inner as UsageMessageT;
  const dev = cat.message as ElevatorUsageMessageT | EscalatorUsageMessageT | null;
  if (!dev) return null;
  const leaf = "usage" in dev ? (dev as ElevatorUsageMessageT).usage : null;
  if (!leaf) return null;
  const variant: MessageVariant =
    "usageType" in dev && (dev as ElevatorUsageMessageT).usageType === UsageVariant.ValuedUsage
      ? "valued"
      : "normal";
  return { variant, enumValue: (leaf as NormalUsageT).message };
}

function extractGroupSupervisoryStatus(inner: flatbuffers.IGeneratedObject) {
  const cat = inner as GroupSupervisoryStatusMessageT;
  const dev = cat.message as
    ElevatorGroupSupervisoryStatusMessageT | EscalatorGroupSupervisoryStatusMessageT | null;
  if (!dev) return null;
  const leaf = "status" in dev ? (dev as ElevatorGroupSupervisoryStatusMessageT).status : null;
  if (!leaf) return null;
  const variant: MessageVariant =
    "statusType" in dev &&
    (dev as ElevatorGroupSupervisoryStatusMessageT).statusType ===
      GroupSupervisoryStatusVariant.ValuedGroupSupervisoryStatus
      ? "valued"
      : "normal";
  return {
    variant,
    enumValue: (leaf as NormalGroupSupervisoryStatusT).message,
  };
}

function extractGroupControl(inner: flatbuffers.IGeneratedObject) {
  const cat = inner as GroupControlMessageT;
  const dev = cat.message as ElevatorGroupControlMessageT | EscalatorGroupControlMessageT | null;
  if (!dev) return null;
  const leaf = "control" in dev ? (dev as ElevatorGroupControlMessageT).control : null;
  if (!leaf) return null;
  const variant: MessageVariant =
    "controlType" in dev &&
    (dev as ElevatorGroupControlMessageT).controlType === GroupControlVariant.ValuedGroupControl
      ? "valued"
      : "normal";
  return { variant, enumValue: (leaf as NormalGroupControlT).message };
}

function extractControllerInfo(inner: flatbuffers.IGeneratedObject) {
  const cat = inner as ControllerInfoMessageT;
  const dev = cat.message as
    ElevatorControllerInfoMessageT | EscalatorControllerInfoMessageT | null;
  if (!dev) return null;
  const leaf = "info" in dev ? (dev as ElevatorControllerInfoMessageT).info : null;
  if (!leaf) return null;
  const variant: MessageVariant =
    "infoType" in dev &&
    (dev as ElevatorControllerInfoMessageT).infoType === ControllerInfoVariant.ValuedControllerInfo
      ? "valued"
      : "normal";
  return { variant, enumValue: (leaf as NormalControllerInfoT).messageType };
}

// =====================================================================
// Registry keyed by generated MessageContent enum — single source of truth
// =====================================================================

const CATEGORY_SPECS = new Map<MessageContent, CategorySpec>([
  [
    MessageContent.Status_StatusMessage,
    {
      encodedMessageContent: EncodedMessageContent.BitMaskMessage,
      buildInner: buildStatus,
      extractEnum: extractStatus,
    },
  ],
  [
    MessageContent.Traffic_TrafficMessage,
    {
      encodedMessageContent: EncodedMessageContent.BitMaskMessage,
      buildInner: buildTraffic,
      extractEnum: extractTraffic,
    },
  ],
  [
    MessageContent.Fault_FaultMessage,
    {
      encodedMessageContent: EncodedMessageContent.BitMaskMessage,
      buildInner: buildFault,
      extractEnum: extractFault,
    },
  ],
  [
    MessageContent.Control_ControlMessage,
    {
      encodedMessageContent: EncodedMessageContent.BitMaskMessage,
      buildInner: buildControl,
      extractEnum: extractControl,
    },
  ],
  [
    MessageContent.Service_ServiceMessage,
    {
      encodedMessageContent: EncodedMessageContent.OneHotMessage,
      buildInner: buildService,
      extractEnum: extractService,
    },
  ],
  [
    MessageContent.Usage_UsageMessage,
    {
      encodedMessageContent: EncodedMessageContent.OneHotMessage,
      buildInner: buildUsage,
      extractEnum: extractUsage,
    },
  ],
  [
    MessageContent.GroupSupervisoryStatus_GroupSupervisoryStatusMessage,
    {
      encodedMessageContent: EncodedMessageContent.BitMaskMessage,
      buildInner: buildGroupSupervisoryStatus,
      extractEnum: extractGroupSupervisoryStatus,
    },
  ],
  [
    MessageContent.GroupControl_GroupControlMessage,
    {
      encodedMessageContent: EncodedMessageContent.BitMaskMessage,
      buildInner: buildGroupControl,
      extractEnum: extractGroupControl,
    },
  ],
  [
    MessageContent.ControllerInfo_ControllerInfoMessage,
    {
      encodedMessageContent: EncodedMessageContent.BitMaskMessage,
      buildInner: buildControllerInfo,
      extractEnum: extractControllerInfo,
    },
  ],
]);

// =====================================================================
// Public API
// =====================================================================

/**
 * Build a full ValuedMessageT from a composite key string.
 *
 * @param key       Composite key, e.g. "status:normal:7"
 * @param active    Whether the message is active
 * @param deviceType  Device type for the device-specific wrapper (default: elevator)
 * @param encodingOverride  Force a specific encoding wrapper (OneHot/BitMask).
 *                          If undefined or NONE, falls back to the category's
 *                          spec default. Use to support fields that let users
 *                          pick the wrapper independently of category convention
 *                          (e.g. Channel.heartbeat_message encoding selector).
 * @returns Built ValuedMessageT, or null if key is invalid
 */
export function compositeKeyToValuedMessageT(
  key: string,
  active: boolean,
  deviceType: DeviceType = "elevator",
  encodingOverride?: EncodedMessageContent,
): ValuedMessageT | null {
  const parsed = parseCompositeKey(key);
  if (!parsed) return null;

  const mc = categoryKeyToMessageContent(parsed.categoryKey);
  if (mc === MessageContent.NONE) return null;

  const spec = CATEGORY_SPECS.get(mc);
  if (!spec) return null;

  const innerMsg = spec.buildInner(parsed.enumValue, parsed.variant, deviceType);

  const encoding = encodingOverride || spec.encodedMessageContent;

  const encodedMsg =
    encoding === EncodedMessageContent.BitMaskMessage
      ? new BitMaskMessageT(mc, innerMsg as BitMaskMessageT["message"])
      : new OneHotMessageT(mc, innerMsg as OneHotMessageT["message"]);

  const msg = new MessageT(encoding, encodedMsg);
  return new ValuedMessageT(msg, active);
}

/**
 * Build a MessageT (without the ValuedMessage wrapper) from a composite key.
 * Used when a field stores Ln2.Message directly (e.g. Channel.heartbeat_message).
 */
export function compositeKeyToMessageT(
  key: string,
  deviceType: DeviceType = "elevator",
  encodingOverride?: EncodedMessageContent,
): MessageT | null {
  return compositeKeyToValuedMessageT(key, false, deviceType, encodingOverride)?.msg ?? null;
}

/**
 * Extract a composite key from a MessageT.
 * Inverse of compositeKeyToMessageT.
 */
export function messageTToCompositeKey(msg: MessageT): string | null {
  return valuedMessageTToCompositeKey(new ValuedMessageT(msg, false));
}

/**
 * Extract a composite key from a ValuedMessageT using per-category
 * strongly-typed extractors (no duck typing).
 */
export function valuedMessageTToCompositeKey(vm: ValuedMessageT): string | null {
  if (!vm.msg) return null;

  const encoded = vm.msg.content;
  if (!encoded) return null;

  const mc = (encoded as BitMaskMessageT | OneHotMessageT).messageType;
  if (mc === undefined || mc === MessageContent.NONE) return null;

  const categoryKey = messageContentToCategoryKey(mc);
  if (!categoryKey) return null;

  const spec = CATEGORY_SPECS.get(mc);
  if (!spec) return null;

  const categoryMessage = (encoded as BitMaskMessageT | OneHotMessageT).message;
  if (!categoryMessage) return null;

  const result = spec.extractEnum(categoryMessage);
  if (!result) return null;

  return `${categoryKey}:${result.variant}:${result.enumValue}`;
}
